import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

// Auth must survive an immediate SSO navigation, even before Vue's next render.
const useAuthStorage = (key: string) => {
  let initialValue: Any = null

  try {
    initialValue = JSON.parse(localStorage.getItem(key) || 'null')?.raw ?? null
  } catch {
    localStorage.removeItem(key)
  }

  const value = ref(initialValue)
  watch(
    value,
    (raw) => {
      if (raw === null) {
        localStorage.removeItem(key)
      } else {
        localStorage.setItem(key, JSON.stringify({ raw }))
      }
    },
    { flush: 'sync' },
  )

  return value
}

/**
 * Decode JWT payload without third-party dependency.
 * Returns null if the token is not a valid JWT.
 */
const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.replace(/^Bearer\s+/i, '').split('.')
    if (parts.length !== 3) return null

    const payloadPart = parts[1]
    if (!payloadPart) return null

    const bytes = Uint8Array.from(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')), (char) =>
      char.charCodeAt(0),
    )
    const payload = JSON.parse(new TextDecoder().decode(bytes))

    return typeof payload === 'object' && payload !== null ? payload : null
  } catch {
    return null
  }
}

/** Seconds before actual expiry to trigger refresh (1 minute buffer). */
const EXPIRY_BUFFER_SECONDS = 60

/**
 * Check if a JWT is expired or about to expire within EXPIRY_BUFFER_SECONDS.
 * Returns true if the token is missing, unparseable, or expired.
 */
export const isTokenExpired = (token: unknown, bufferSeconds = EXPIRY_BUFFER_SECONDS): boolean => {
  if (typeof token !== 'string') return true

  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) return true

  return Date.now() >= (payload.exp - bufferSeconds) * 1000
}

interface AuthTokenPayload {
  token_type?: unknown
  access_token?: unknown
}

const isValidBearerToken = (value: unknown): value is string => {
  if (typeof value !== 'string') return false

  const accessToken = /^Bearer (\S+)$/i.exec(value)?.[1]

  return Boolean(accessToken && !['undefined', 'null'].includes(accessToken.toLowerCase()))
}

export const toBearerToken = (data: unknown) => {
  if (!data || typeof data !== 'object') return null

  const { token_type: tokenType, access_token: accessToken } = data as AuthTokenPayload

  if (
    typeof tokenType !== 'string' ||
    tokenType.trim().toLowerCase() !== 'bearer' ||
    typeof accessToken !== 'string'
  ) {
    return null
  }

  const bearerToken = `Bearer ${accessToken.trim()}`

  return isValidBearerToken(bearerToken) ? bearerToken : null
}

export const useUserStore = defineStore('userInfo', () => {
  const userInfo = useAuthStorage('userInfo')

  const getUserInfo = () => userInfo.value

  const setUserInfo = (newValue: AnyRecord) => (userInfo.value = newValue)

  const clear = () => (userInfo.value = null)

  return {
    clear,
    getUserInfo,
    setUserInfo,
  }
})

export const useTokenStore = defineStore('token', () => {
  const token = useAuthStorage('token')

  const getToken = () => token.value

  const hasValidToken = () => isValidBearerToken(token.value)

  const setToken = (data: unknown) => (token.value = toBearerToken(data))

  const clear = () => (token.value = null)

  return {
    clear,
    getToken,
    hasValidToken,
    setToken,
  }
})

export const useRefreshTokenStore = defineStore('refreshToken', () => {
  const refreshToken = useAuthStorage('refresh_token')

  const getRefreshToken = () => refreshToken.value

  const hasValidRefreshToken = () =>
    typeof refreshToken.value === 'string' && refreshToken.value.trim().length > 0

  const setRefreshToken = (data: unknown) => {
    if (data && typeof data === 'object') {
      const { refresh_token: token } = data as { refresh_token?: unknown }
      refreshToken.value = typeof token === 'string' ? token : null
    } else if (typeof data === 'string') {
      refreshToken.value = data
    } else {
      refreshToken.value = null
    }
  }

  const clear = () => (refreshToken.value = null)

  return {
    clear,
    getRefreshToken,
    hasValidRefreshToken,
    setRefreshToken,
  }
})
